import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import LoadingButton from "../components/LoadingButton";
import { useRealtime } from "../contexts/RealtimeContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  X,
  FileText,
  Zap,
  Radar,
  CheckCircle,
  AlertCircle,
  Activity,
  ArrowRight,
  Download,
  Clock,
  Layout,
  Sparkles,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { track } from "../utils/track";
import EmptyReadNotice from "../components/EmptyReadNotice";
import DuplicateNotice from "../components/DuplicateNotice";
import ReferralNudge from "../components/ReferralNudge";
import OutOfCreditsNotice from "../components/OutOfCreditsNotice";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const STATUS_LABELS = {
  queued: "En cola",
  processing: "Procesando",
  completed: "Completado",
  error: "Error",
};

const STATUS_STYLES = {
  queued: "bg-yellow-50 text-yellow-700 border-yellow-200",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

function mapEventToResult(event) {
  if (!event) return null;
  const fields = Array.isArray(event.payload?.extractedJson?.fields)
    ? event.payload.extractedJson.fields
    : [];
  const filledFields = fields.filter(
    (f) => f.value !== null && f.value !== undefined
  );
  return {
    filename: event.payload?.filename,
    tokensUsed: event.payload?.tokensUsed,
    tokensRemaining: event.payload?.tokensRemaining,
    processingTime: event.payload?.processingTime || 0,
    summary: event.payload?.extractedJson?.summary || {
      fields_with_values: filledFields.length,
      fields_null: fields.length - filledFields.length,
    },
    extractedFields: fields,
    extractedJson: event.payload?.extractedJson,
  };
}

function formatTimestamp(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) {
    return "";
  }
}

export default function UploadProcessor() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [scanners, setScanners] = useState([]);
  const [selected, setSelected] = useState("");
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [tokens, setTokens] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [outOfCredits, setOutOfCredits] = useState(null); // { required, available } tras un 402 de páginas
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs, connectionState, registerResultOrigin } = useRealtime();
  const [pendingResultId, setPendingResultId] = useState(null);
  const [focusedResultId, setFocusedResultId] = useState(null);

  const realtimeJobs = useMemo(() => {
    if (!jobs || typeof jobs.entries !== "function") return [];
    return Array.from(jobs.entries())
      .map(([resultId, data]) => ({ resultId, ...data }))
      .sort((a, b) => {
        const aTime = new Date(a.lastEvent?.timestamp || 0).getTime();
        const bTime = new Date(b.lastEvent?.timestamp || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 6);
  }, [jobs]);

  useEffect(() => {
    if (!user) return;
    api.listScanners().then((data) => {
      setScanners(data);
      if (data?.length === 1) {
        setSelected(String(data[0].id));
      }
    });
    api
      .getTokens()
      .then((data) => setTokens(data?.tokens_balance ?? data ?? 0));
  }, [user]);

  useEffect(() => {
    if (!location.state?.focusResultId) return;
    setFocusedResultId(location.state.focusResultId);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  const submit = async () => {
    if (!selected || !file) {
      setError(t("uploadProcessor.select_scanner_file"));
      return;
    }

    setProcessing(true);
    setStatus(t("uploadProcessor.processing"));
    setError(null);
    setOutOfCredits(null);
    setResults(null);

    try {
      // start progress simulation
      setProgress(3);
      progressRef.current = setInterval(() => {
        setProgress((p) =>
          Math.min(95, Math.max(p + Math.random() * 12, p + 3))
        );
      }, 350);
      const response = await api.processUpload(selected, file);

      if (response.success) {
        setProgress(100);
        setPendingResultId(response.resultId);
        setFocusedResultId(response.resultId);
        registerResultOrigin?.({
          resultId: response.resultId,
          scannerId: selected,
          scannerName:
            scanners.find((s) => String(s.id) === String(selected))?.name ||
            null,
        });
        setStatus(
          t("uploadProcessor.waiting_realtime", {
            defaultValue: "Document queued. Updates via realtime...",
          })
        );
        setError(null);
        setResults(null);
        setFile(null);
        setPreviewUrl(null);
      } else {
        setError(response.message || t("uploadProcessor.failed"));
      }
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 402 && data.next_action?.type !== "billing_portal") {
        // Sin páginas suficientes: aviso con salida (planes / paquete), no el mensaje técnico del servidor.
        setOutOfCredits({ required: data.tokensRequired, available: data.tokensAvailable });
        setStatus("");
        return;
      }
      const errorMessage =
        data.message ||
        err.message ||
        t("uploadProcessor.error_processing");
      setError(errorMessage);
      setStatus(t("uploadProcessor.failed"));
    } finally {
      clearInterval(progressRef.current);
      setTimeout(() => setProgress(0), 300);
      setProcessing(false);
    }
  };

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) {
      if (f.size > MAX_FILE_SIZE) {
        setError(t("uploadProcessor.file_too_large"));
        return;
      }
      setFile(f);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    if (file.type && file.type.startsWith("image/")) {
      const u = URL.createObjectURL(file);
      setPreviewUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setPreviewUrl(null);
  }, [file]);

  useEffect(() => {
    if (!pendingResultId) return;
    if (!jobs || typeof jobs.get !== "function") return;
    const job = jobs.get(pendingResultId);
    if (!job?.lastEvent) return;
    const evt = job.lastEvent;

    if (evt.eventType === "completed") {
      const mapped = mapEventToResult(evt);
      if (mapped) {
        setResults({
          ...mapped,
          filename: mapped.filename || file?.name,
          tokensUsed: mapped.tokensUsed,
          tokensRemaining: mapped.tokensRemaining ?? tokens,
        });
      }
      if (evt.payload?.tokensRemaining !== undefined) {
        setTokens(evt.payload.tokensRemaining);
      } else {
        api
          .getTokens()
          .then((data) => setTokens(data?.tokens_balance ?? data ?? 0))
          .catch(() => {});
      }
      setStatus(
        t("uploadProcessor.success", { defaultValue: "Document Processed" })
      );
      setError(null);
      setPendingResultId(null);
      setFocusedResultId(evt.resultId);
    } else if (evt.eventType === "error") {
      setError(
        evt.payload?.errorMessage ||
          t("uploadProcessor.error_processing", {
            defaultValue: "Error processing document",
          })
      );
      setStatus(t("uploadProcessor.failed", { defaultValue: "Error" }));
      setPendingResultId(null);
      setFocusedResultId(evt.resultId);
    }
  }, [pendingResultId, jobs, t, tokens, file]);

  // Helper function to normalize field label (use name if label seems to be a description)
  const normalizeFieldLabel = (field) => {
    if (!field.label || !field.name) return field.name || field.label;
    // If label is very long (>50 chars) or contains common description words, use name instead
    const label = String(field.label).trim();
    const isDescription =
      label.length > 50 ||
      label.toLowerCase().includes("the ") ||
      label.toLowerCase().includes("assigned") ||
      label.toLowerCase().includes("billed") ||
      label.toLowerCase().includes("including");
    return isDescription ? field.name : label;
  };

  const cleanJsonForUser = (data) => {
    if (!data) return null;
    const cleaned = {
      scanner_name: data.scanner_name,
      extracted_at: data.extracted_at,
      filename: data.filename,
      fields: (data.fields || data.extractedFields || []).map((field) => ({
        name: field.name,
        label: normalizeFieldLabel(field), // Normalize label to ensure consistency
        value: field.value,
        confidence: field.confidence,
        source_fragment: field.source_fragment,
        ...(field.normalization_notes &&
          field.normalization_notes.trim() && {
            normalization_notes: field.normalization_notes,
          }),
      })),
    };
    return cleaned;
  };

  const downloadResults = (format = "json") => {
    track("export", { format, where: "upload_page" });
    if (!results) return;

    let dataStr, mimeType, extension;

    switch (format) {
      case "json":
        const cleanedData = cleanJsonForUser(results);
        dataStr = JSON.stringify(cleanedData, null, 2);
        mimeType = "application/json";
        extension = "json";
        break;
      case "csv":
        dataStr = convertToCSV(results.extractedFields);
        mimeType = "text/csv;charset=utf-8;";
        extension = "csv";
        break;
      default:
        return;
    }

    const dataBlob = new Blob([dataStr], { type: mimeType });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `extraction-${
      results.filename || results.requestId || "results"
    }.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (fields) => {
    if (!fields || fields.length === 0) return "";
    const headers = [
      "Field Name",
      "Label",
      "Value",
      "Confidence (%)",
      "Source Fragment",
      "Normalization Notes",
    ];
    const rows = fields.map((field) => [
      field.name || "",
      field.label || "",
      field.value !== null && field.value !== undefined
        ? String(field.value)
        : "N/A",
      field.confidence ? Math.round(field.confidence * 100) : 0,
      field.source_fragment || "",
      field.normalization_notes || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    return csvContent;
  };

  const resetForm = () => {
    setFile(null);
    setSelected("");
    setResults(null);
    setError(null);
    setStatus("");
    setPendingResultId(null);
    setFocusedResultId(null);
  };

  const estimateTokensFromSize = (f) => {
    if (!f) return 0;
    const sizeMB = f.size / 1024 / 1024;
    return Math.max(1, Math.ceil(sizeMB * 2));
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 max-w-7xl mx-auto space-y-8 min-h-screen"
    >
      <div className="text-center space-y-2 mb-10">
        <motion.h2
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-4xl font-extrabold text-gray-900 tracking-tight"
        >
          {t("uploadProcessor.title") || "Smart Upload"}
        </motion.h2>
        <motion.p
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-lg text-gray-500 max-w-2xl mx-auto"
        >
          {t("uploadProcessor.subtitle") ||
            "Extract data from any document instantly using our AI scanners."}
        </motion.p>
      </div>

      <AnimatePresence mode="wait">
        {!results ? (
          <motion.div
            key="upload-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            {/* Left Column: Upload & Scanner Selection */}
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/60 p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-32 bg-primary-50/50 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

                <div className="relative z-10">
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">
                      {t("uploadProcessor.select_scanner")}
                    </label>
                    <div className="relative">
                      <select
                        className="w-full appearance-none bg-gray-50 border border-gray-200 text-gray-900 text-lg rounded-2xl px-5 py-4 focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm hover:bg-white transition-all duration-200 cursor-pointer"
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                        disabled={processing}
                      >
                        <option value="">
                          {t("uploadProcessor.select_scanner_placeholder") ||
                            "Select a Scanner..."}
                        </option>
                        {scanners.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500">
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className="relative group"
                  >
                    <input
                      id="fileinput"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.gif"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        if (f.size > MAX_FILE_SIZE) {
                          setError(t("uploadProcessor.file_too_large"));
                          return;
                        }
                        setFile(f);
                        setError(null);
                      }}
                      disabled={processing}
                      className="hidden"
                    />

                    <motion.div
                      animate={{
                        scale: dragActive ? 1.02 : 1,
                        borderColor: dragActive
                          ? "var(--primary-500)"
                          : "var(--gray-200)",
                      }}
                      className={cn(
                        "w-full aspect-[2/1] min-h-[300px] border-2 border-dashed rounded-3xl flex flex-col items-center justify-center p-8 transition-all duration-300 cursor-pointer relative overflow-hidden",
                        dragActive
                          ? "bg-primary-50/50 border-primary-500"
                          : "bg-gray-50/50 border-gray-200 hover:border-primary-300 hover:bg-gray-50"
                      )}
                      onClick={() =>
                        document.getElementById("fileinput")?.click()
                      }
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-primary-50/30 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                      {file ? (
                        <div className="relative z-10 text-center w-full max-w-sm mx-auto">
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4 text-primary-600"
                          >
                            <FileText className="w-10 h-10" />
                          </motion.div>
                          <h3 className="text-xl font-bold text-gray-900 mb-1 truncate px-4">
                            {file.name}
                          </h3>
                          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
                            <span className="bg-gray-200 px-2 py-0.5 rounded text-gray-700 font-medium">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                            <span>•</span>
                            <span className="text-primary-600 font-medium flex items-center gap-1">
                              <Zap className="w-3 h-3" />~
                              {estimateTokensFromSize(file)} Est. Tokens
                            </span>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(null);
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors text-sm font-medium flex items-center justify-center gap-1 mx-auto py-2 px-4 hover:bg-red-50 rounded-lg"
                          >
                            <X className="w-4 h-4" />{" "}
                            {t("uploadProcessor.remove")}
                          </button>
                        </div>
                      ) : (
                        <div className="relative z-10 text-center">
                          <div className="w-20 h-20 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                            <Upload className="w-10 h-10 text-gray-400 group-hover:text-primary-600 transition-colors" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {t("uploadProcessor.drag_drop")}
                          </h3>
                          <p className="text-gray-500 text-sm max-w-xs mx-auto">
                            {t("uploadProcessor.file_hint")}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* Progress Bar */}
                  {processing && (
                    <div className="mt-6">
                      <div className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                        <span>{t("uploadProcessor.processing")}</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-primary-500 to-primary-600"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {outOfCredits && (
                    <div className="mt-6">
                      <OutOfCreditsNotice
                        required={outOfCredits.required}
                        available={outOfCredits.available}
                        isFreePlan={String(user?.subscription_plan || user?.subscription?.plan || "free").toLowerCase() === "free"}
                        where="upload"
                      />
                    </div>
                  )}

                  {/* Error & Status Messages */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-6"
                      >
                        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
                          <AlertCircle className="w-5 h-5 shrink-0" />
                          <span className="font-medium">{error}</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Button */}
                  <div className="mt-8 flex justify-end">
                    <LoadingButton
                      onClick={submit}
                      loading={processing}
                      disabled={!selected || !file || processing}
                      className="w-full sm:w-auto bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white min-w-[200px] py-4 rounded-xl font-bold shadow-lg shadow-primary-500/25 transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5"
                    >
                      {processing
                        ? t("uploadProcessor.processing")
                        : t("uploadProcessor.start_extraction")}
                    </LoadingButton>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Stats & Status */}
            <div className="lg:col-span-4 space-y-6">
              {/* Token Balance */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-3xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-24 bg-primary-500/20 rounded-full blur-3xl -mr-12 -mt-12 pointer-events-none" />
                <div className="relative z-10 flex items-center justify-between mb-8">
                  <div className="p-3 bg-white/10 rounded-xl">
                    <Zap className="w-6 h-6 text-yellow-400" />
                  </div>
                  <button
                    onClick={() => navigate("/tokens")}
                    className="text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/20 px-3 py-1 rounded-lg transition-colors"
                  >
                    {t("uploadProcessor.top_up")}
                  </button>
                </div>
                <div className="relative z-10">
                  <div className="text-gray-400 text-sm font-medium mb-1">
                    {t("uploadProcessor.available_tokens")}
                  </div>
                  <div className="text-4xl font-extrabold tracking-tight">
                    {tokens.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Live Status */}
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 h-[400px] flex flex-col">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-gray-500" />
                    <h3 className="font-bold text-gray-900">
                      {t("uploadProcessor.live_activity")}
                    </h3>
                  </div>
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full ring-4 ring-opacity-20",
                      connectionState === "open"
                        ? "bg-green-500 ring-green-500"
                        : connectionState === "connecting"
                        ? "bg-yellow-500 ring-yellow-500"
                        : "bg-red-500 ring-red-500"
                    )}
                  />
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-2">
                  {realtimeJobs.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 text-center">
                      <Radar className="w-12 h-12 mb-2 opacity-20" />
                      <p className="text-sm font-medium">
                        {t("uploadProcessor.waiting_for_activity")}
                      </p>
                    </div>
                  ) : (
                    realtimeJobs.map(({ resultId, lastEvent }) => {
                      const statusKey =
                        lastEvent?.payload?.status || lastEvent?.eventType;
                      const statusLabel = STATUS_LABELS[statusKey] || statusKey;
                      const pillClass =
                        STATUS_STYLES[statusKey] || STATUS_STYLES.queued;

                      return (
                        <motion.div
                          key={resultId}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="bg-gray-50 p-3 rounded-xl border border-gray-100"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-bold text-gray-700 truncate max-w-[120px]">
                              {lastEvent?.payload?.filename ||
                                resultId.slice(0, 8)}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {formatTimestamp(lastEvent?.timestamp)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "text-[10px] font-bold px-2 py-0.5 rounded-md",
                                pillClass
                              )}
                            >
                              {statusLabel}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="results"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl mx-auto"
            data-track-area="upload_result"
          >
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Result Header */}
              <div className="p-8 border-b border-gray-100 bg-gray-50/80 backdrop-blur flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 text-green-600 rounded-xl">
                    <CheckCircle className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {t("uiFixes.success")}
                    </h3>
                    <p className="text-gray-500">
                      {results.filename} • {results.processingTime}s
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => downloadResults("json")}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 shadow-sm rounded-xl font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                  >
                    <Download className="w-4 h-4" /> JSON
                  </button>
                  <button
                    onClick={() => downloadResults("csv")}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 shadow-sm rounded-xl font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                  >
                    <Download className="w-4 h-4" /> CSV
                  </button>
                </div>
              </div>

              <div className="px-6 pt-6">
                <EmptyReadNotice extractedJson={results.extractedJson} t={t} className="" />
                <DuplicateNotice extractedJson={results.extractedJson} t={t} className="mt-3" />
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {results.summary?.fields_with_values || 0}
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Found
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-400">
                    {results.summary?.fields_null || 0}
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {t("uiFixes.missing")}
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-primary-600">
                    {results.tokensUsed}
                  </div>
                  <div className="text-xs font-bold text-primary-400 uppercase tracking-wider">
                    Cost
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {(
                      ((results.summary?.fields_with_values || 0) /
                        Math.max(1, results.extractedFields?.length || 1)) *
                      100
                    ).toFixed(0)}
                    %
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Score
                  </div>
                </div>
              </div>

              {/* Action Footer */}
              <div className="p-8 bg-gray-50 flex justify-center">
                <button
                  onClick={resetForm}
                  className="flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Process Another Document <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <ReferralNudge />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.main>
  );
}
